// Release B · B0-Closeout — Owner Inbox front-end page.
// Route: /admin/owner/inbox
// Strict-language rule enforced in chip labels:
//   provider_accepted ≠ delivered. Distinct colors + copy.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AdminAppShell } from "../../design-system/shells/AdminAppShell";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import InformationIntake, { AttachmentsPanel } from "../../design-system/intake/InformationIntake";
// Release C-1 — Open in Microsoft 365 Mail from any Owner Inbox row detail.
import OpenInMicrosoftMailButton from "../../design-system/microsoft365/OpenInMicrosoftMailButton";

const KINDS = [
  "", "quote_request", "contact_request", "portal_access_request",
  "applicant_questionnaire", "client_request", "vendor_request",
  "subcontractor_request", "approval_request", "website_inquiry",
  "delivery_failure",
];
const STATES = [
  "", "new", "in_review", "info_requested", "awaiting_owner",
  "assigned", "closed", "duplicate",
];

// Strict-language chip mapping — provider_accepted must be visually distinct
// from delivered. `sent` (legacy in-app) rendered as "in-app recorded."
const CHIP_COLORS = {
  queued:            { bg: "#F1F5F9", fg: "#334155", border: "#CBD5E1" },
  provider_accepted: { bg: "#FEF3C7", fg: "#92400E", border: "#FDE68A" }, // amber
  delivered:         { bg: "#DCFCE7", fg: "#166534", border: "#86EFAC" }, // green
  deferred:          { bg: "#DBEAFE", fg: "#1E40AF", border: "#93C5FD" },
  bounced:           { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" },
  blocked:           { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" },
  dropped:           { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" },
  failed:            { bg: "#FEE2E2", fg: "#991B1B", border: "#FCA5A5" },
  authorization_required: { bg: "#F1F5F9", fg: "#334155", border: "#CBD5E1" },
  sent:              { bg: "#E0E7FF", fg: "#3730A3", border: "#C7D2FE" }, // in-app only
};
const CHIP_LABEL = {
  queued: "queued",
  provider_accepted: "provider_accepted",
  delivered: "delivered",
  deferred: "deferred",
  bounced: "bounced",
  blocked: "blocked",
  dropped: "dropped",
  failed: "failed",
  authorization_required: "authorization_required",
  sent: "in-app recorded",
};

function DeliveryChip({ state }) {
  const s = state || "queued";
  const c = CHIP_COLORS[s] || CHIP_COLORS.queued;
  return (
    <span data-testid={`delivery-chip-${s}`} style={{
      display: "inline-block", padding: "2px 10px", borderRadius: 9999,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
      textTransform: "lowercase",
    }}>{CHIP_LABEL[s] || s}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function OwnerInbox() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState(null);
  const [kind, setKind] = useState("");
  const [state, setState] = useState("");
  const [unread, setUnread] = useState("");
  const [priority, setPriority] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [note, setNote] = useState("");
  const [attachReload, setAttachReload] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (kind) params.set("kind", kind);
      if (state) params.set("workflow_state", state);
      if (unread) params.set("unread", unread);
      if (priority) params.set("priority", priority);
      if (q) params.set("q", q);
      params.set("limit", "50");
      const [list, c] = await Promise.all([
        api.get(`/owner/inbox?${params.toString()}`),
        api.get(`/owner/inbox/counts`),
      ]);
      setRows(list.data.items || []); setTotal(list.data.total || 0);
      setCounts(c.data);
    } catch (e) {
      toast.error("Failed to load Owner Inbox");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind, state, unread, priority]);

  const openDetail = async (row) => {
    setSelected(row);
    try {
      const r = await api.get(`/owner/inbox/${row.id}`);
      setDetail(r.data);
    } catch (e) { toast.error("Failed to load detail"); }
  };

  const markRead = async (row) => {
    try { await api.post(`/owner/inbox/${row.id}/mark-read`); await load(); if (selected?.id === row.id) openDetail(row); }
    catch { toast.error("mark-read failed"); }
  };
  const markUnread = async (row) => {
    try { await api.post(`/owner/inbox/${row.id}/mark-unread`); await load(); }
    catch { toast.error("mark-unread failed"); }
  };
  const closeItem = async (row) => {
    try { await api.post(`/owner/inbox/${row.id}/close`, { workflow_state: "closed" }); await load(); toast.success("Closed"); }
    catch { toast.error("close failed"); }
  };
  const resend = async (row) => {
    try {
      const r = await api.post(`/owner/inbox/${row.id}/resend-notification`);
      toast.success(`Resend queued · outbox=${(r.data.outbox_id || "").slice(0, 8)}…`);
      if (selected?.id === row.id) openDetail(row);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.code === "resend_rate_limited") toast.warning(`Rate-limited — wait ${d.retry_after_seconds}s`);
      else if (d?.code === "forbidden_owner_only") toast.error("Owner-only action");
      else toast.error(d?.message || "resend failed");
    }
  };
  const addNote = async () => {
    if (!note.trim() || !selected) return;
    try { await api.post(`/owner/inbox/${selected.id}/note`, { body: note });
          setNote(""); openDetail(selected); toast.success("Note added"); }
    catch { toast.error("note failed"); }
  };

  return (
    <AdminAppShell current="/admin/owner/inbox">
      <div data-testid="owner-inbox-page" style={{ padding: 24 }}>
        <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h1 style={{ fontSize: 26, fontWeight: 700, margin: 0 }}>Owner Requests Inbox</h1>
            <div style={{ color: "#475569", fontSize: 13, marginTop: 4 }}>
              Every public/business submission arriving at ConstructionOS. Persistence-first: authoritative record always exists before any notification attempt.
            </div>
          </div>
        </div>

        {counts && (
          <div data-testid="owner-inbox-counts" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 12, marginBottom: 16 }}>
            {[
              ["new_requests", "New", "#0f172a"],
              ["awaiting_owner_review", "Awaiting Owner", "#7c2d12"],
              ["questionnaires_awaiting_review", "Questionnaires", "#1e3a8a"],
              ["failed_notifications", "Delivery failures", "#991b1b"],
              ["needing_more_info", "Needs info", "#4b5563"],
              ["total_unread", "Unread", "#111827"],
            ].map(([k, label, color]) => (
              <Card key={k} data-testid={`inbox-count-${k}`}>
                <CardContent style={{ padding: 14 }}>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>{label}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color }}>{counts[k] ?? 0}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
          <select data-testid="filter-kind" value={kind} onChange={(e) => setKind(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            {KINDS.map(k => <option key={k} value={k}>{k || "All kinds"}</option>)}
          </select>
          <select data-testid="filter-state" value={state} onChange={(e) => setState(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            {STATES.map(s => <option key={s} value={s}>{s || "All states"}</option>)}
          </select>
          <select data-testid="filter-unread" value={unread} onChange={(e) => setUnread(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            <option value="">Read + Unread</option>
            <option value="true">Unread only</option>
            <option value="false">Read only</option>
          </select>
          <select data-testid="filter-priority" value={priority} onChange={(e) => setPriority(e.target.value)} style={{ padding: "6px 10px", border: "1px solid #cbd5e1", borderRadius: 6 }}>
            <option value="">All priorities</option>
            <option value="high">high</option>
            <option value="normal">normal</option>
            <option value="low">low</option>
          </select>
          <Input data-testid="filter-search" placeholder="Search name / email / org…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} style={{ maxWidth: 320 }} />
          <Button data-testid="filter-apply" onClick={load}>Search</Button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: selected ? "1.4fr 1.2fr" : "1fr", gap: 16 }}>
          <Card>
            <CardContent style={{ padding: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }} data-testid="inbox-table">
                <thead>
                  <tr style={{ textAlign: "left", background: "#f8fafc" }}>
                    <th style={{ padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>Kind</th>
                    <th style={{ padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>Applicant</th>
                    <th style={{ padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>State</th>
                    <th style={{ padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>Received</th>
                    <th style={{ padding: 10, fontSize: 11, textTransform: "uppercase", color: "#64748b" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading && <tr><td colSpan={5} style={{ padding: 16, color: "#64748b" }}>Loading…</td></tr>}
                  {!loading && rows.length === 0 && <tr><td colSpan={5} style={{ padding: 16, color: "#64748b" }}>No inbox items match these filters.</td></tr>}
                  {rows.map((r) => (
                    <tr key={r.id} data-testid={`inbox-row-${r.id}`} onClick={() => openDetail(r)}
                        style={{ borderTop: "1px solid #e5e7eb", cursor: "pointer",
                                 background: r.unread ? "#fefce8" : "white",
                                 fontWeight: r.unread ? 600 : 400 }}>
                      <td style={{ padding: 10 }}>{r.kind}</td>
                      <td style={{ padding: 10 }}>
                        <div>{r.applicant_or_customer_snapshot?.name || "—"}</div>
                        <div style={{ fontSize: 11, color: "#64748b" }}>{r.applicant_or_customer_snapshot?.email}</div>
                      </td>
                      <td style={{ padding: 10, fontSize: 12 }}>
                        <div>{r.workflow_state}</div>
                        {r.delivery_state && (
                          <div style={{ marginTop: 4 }}>
                            <DeliveryChip state={r.delivery_state} />
                          </div>
                        )}
                      </td>
                      <td style={{ padding: 10, fontSize: 12 }}>{fmtDate(r.received_at)}</td>
                      <td style={{ padding: 10 }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {r.unread
                            ? <Button size="sm" variant="outline" data-testid={`inbox-mark-read-${r.id}`} onClick={() => markRead(r)}>Mark read</Button>
                            : <Button size="sm" variant="outline" data-testid={`inbox-mark-unread-${r.id}`} onClick={() => markUnread(r)}>Mark unread</Button>}
                          <Button size="sm" variant="outline" data-testid={`inbox-resend-${r.id}`} onClick={() => resend(r)}>Resend</Button>
                          {!r.closed_at && <Button size="sm" variant="outline" data-testid={`inbox-close-${r.id}`} onClick={() => closeItem(r)}>Close</Button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>

          {selected && detail && (
            <Card data-testid="inbox-detail-drawer">
              <CardContent style={{ padding: 16, maxHeight: "70vh", overflowY: "auto" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>{detail.kind}</div>
                    <div style={{ fontSize: 18, fontWeight: 700 }}>{detail.applicant_or_customer_snapshot?.name || "—"}</div>
                    <div style={{ fontSize: 12, color: "#475569" }}>{detail.applicant_or_customer_snapshot?.email} · {detail.applicant_or_customer_snapshot?.phone || "—"}</div>
                  </div>
                  <button data-testid="inbox-detail-close" onClick={() => { setSelected(null); setDetail(null); }} aria-label="Close detail" style={{ border: 0, background: "transparent", fontSize: 20, cursor: "pointer" }}>×</button>
                </div>
                {/* Release C-1 — Open the linked Microsoft 365 mailbox thread. */}
                <div style={{ marginBottom: 10 }}>
                  <OpenInMicrosoftMailButton
                    surface="owner_inbox_item"
                    recordType="owner_inbox_item"
                    recordId={selected.id}
                  />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, fontSize: 12, marginBottom: 12, color: "#334155" }}>
                  <div>State: <b>{detail.workflow_state}</b></div>
                  <div>Priority: <b>{detail.priority}</b></div>
                  <div>Received: {fmtDate(detail.received_at)}</div>
                  <div>Last activity: {fmtDate(detail.last_activity_at)}</div>
                  <div>Assigned to: {detail.assigned_to || "—"}</div>
                  <div>Source: {detail.source_ref?.collection}/{(detail.source_ref?.id || "").slice(0, 8)}…</div>
                </div>

                <h3 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b", margin: "10px 0 6px" }}>Notification attempts</h3>
                <div data-testid="inbox-detail-attempts" style={{ fontSize: 12 }}>
                  {(detail.notification_attempts || []).length === 0 && <div style={{ color: "#64748b" }}>No attempts yet.</div>}
                  {(detail.notification_attempts || []).map((a) => (
                    <div key={a.id} style={{ borderTop: "1px solid #e5e7eb", padding: "8px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div><b>{a.recipient || a.to || "—"}</b> · <span style={{ color: "#64748b" }}>{a.template_key}</span></div>
                        <DeliveryChip state={a.state} />
                      </div>
                      <div style={{ color: "#475569", marginTop: 2 }}>
                        msg_id: {(a.provider_message_id || "—").slice(0, 34)} · status: {a.provider_status_code || "—"} · attempts: {a.attempt_count ?? a.attempts ?? "—"}
                      </div>
                      <div style={{ color: "#64748b" }}>
                        created: {fmtDate(a.created_at)} · last_event: {a.last_provider_event || "(awaiting delivered event)"} at {fmtDate(a.last_provider_event_at)}
                      </div>
                      {a.send_error && <div style={{ color: "#991b1b", marginTop: 2 }}>error: {a.send_error}</div>}
                    </div>
                  ))}
                </div>

                <h3 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b", margin: "14px 0 6px" }}>Notes / timeline</h3>
                <div data-testid="inbox-detail-notes" style={{ fontSize: 12 }}>
                  {(detail.notes || []).length === 0 && <div style={{ color: "#64748b" }}>No notes yet.</div>}
                  {(detail.notes || []).map((n) => (
                    <div key={n.id} style={{ borderLeft: "3px solid #cbd5e1", padding: "4px 0 4px 8px", margin: "6px 0" }}>
                      <div style={{ color: "#334155" }}>{n.body}</div>
                      <div style={{ color: "#94a3b8", fontSize: 11 }}>{fmtDate(n.created_at)} · {n.author}{n.system ? " (system)" : ""}</div>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                  <Input data-testid="inbox-note-input" placeholder="Add an internal note…" value={note} onChange={(e) => setNote(e.target.value)} />
                  <Button data-testid="inbox-note-submit" onClick={addNote}>Add note</Button>
                </div>

                {/* 3b retrofit — universal InformationIntake for Owner Inbox item. */}
                <div style={{ marginTop: 16, paddingTop: 14, borderTop: "1px solid #e2e8f0" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1, color: "#64748b" }}>Attachments on this inbox item</div>
                    <InformationIntake
                      recordType="owner_inbox_item" recordId={selected.id}
                      category="paste_note" confidentiality="confidential"
                      capabilities={["paste", "upload", "photo", "existing", "manual"]}
                      onChanged={() => setAttachReload((k) => k + 1)}
                    />
                  </div>
                  <AttachmentsPanel recordType="owner_inbox_item" recordId={selected.id} reload={attachReload} />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div style={{ marginTop: 10, fontSize: 11, color: "#64748b" }}>
          Delivery chip legend — <b>provider_accepted</b> is HTTP 202 from SendGrid (<em>not</em> delivered). <b>delivered</b> is set only by a signed <code>delivered</code> webhook event.
        </div>
      </div>
    </AdminAppShell>
  );
}
